import './AllergyList.css'
import type { Allergy } from '../types/allergy'
import { formatDate } from '../utils/locale'

interface AllergyListProps {
  allergies: Allergy[]
  onAdd: () => void
  onEdit: (allergy: Allergy) => void
  onDeactivate: (allergy: Allergy) => void
  readOnly?: boolean
}

export function AllergyList({
  allergies,
  onAdd,
  onEdit,
  onDeactivate,
  readOnly = false,
}: AllergyListProps) {
  const activeAllergies = allergies.filter((a) => a.status === 'active' && !a.isNKDA)
  const inactiveAllergies = allergies.filter((a) => a.status === 'inactive' && !a.isNKDA)

  return (
    <div className="allergy-list">
      <div className="allergy-list-header">
        <div>
          <h2>Allergies & Adverse Reactions</h2>
          <p className="allergy-subtitle">
            Structured capture of true allergies, intolerances, adverse reactions and NKDA for safer prescribing.
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={onAdd}
          disabled={readOnly}
          title={readOnly ? 'Secondary records are read-only' : undefined}
        >
          + Add Allergy / Reaction
        </button>
      </div>

      {activeAllergies.length === 0 && inactiveAllergies.length === 0 && (
        <div className="allergy-empty-state">
          <p>No allergies, intolerances, adverse reactions or NKDA recorded yet.</p>
          <p className="allergy-empty-hint">
            Recording allergies and NKDA helps prevent prescribing errors and improves clinical safety.
          </p>
          <button
            className="btn-primary"
            onClick={onAdd}
            disabled={readOnly}
            title={readOnly ? 'Secondary records are read-only' : undefined}
          >
            Record Allergy or NKDA
          </button>
        </div>
      )}

      {activeAllergies.length > 0 && (
        <section className="allergy-section">
          <div className="section-header">
            <h3>Active Allergies & Reactions</h3>
            <span className="badge">{activeAllergies.length}</span>
          </div>
          <div className="allergy-table" role="table">
            <div className="allergy-table-header" role="row">
              <div className="cell trigger">Trigger</div>
              <div className="cell category">Category</div>
              <div className="cell reaction">Reaction</div>
              <div className="cell severity">Severity</div>
              <div className="cell recorded">Recorded</div>
              <div className="cell actions">Actions</div>
            </div>
            {activeAllergies.map((allergy) => (
              <div
                key={allergy.id}
                className="allergy-table-row"
                role="row"
              >
                <div className="cell trigger">
                  <div className="trigger-main">
                    <span className={`trigger-type-pill trigger-${allergy.triggerType}`}>
                      {allergy.triggerType === 'drug' && 'Drug'}
                      {allergy.triggerType === 'food' && 'Food'}
                      {allergy.triggerType === 'environmental' && 'Environmental'}
                      {allergy.triggerType === 'other' && 'Other'}
                    </span>
                    <span className="trigger-name">{allergy.triggerDisplay}</span>
                  </div>
                  {allergy.triggerCode && (
                    <div className="trigger-code">
                      Code: <span>{allergy.triggerCode}</span>
                    </div>
                  )}
                </div>
                <div className="cell category">
                  <span className={`badge badge-${allergy.category}`}>
                    {allergy.category === 'true-allergy' && 'True Allergy'}
                    {allergy.category === 'intolerance' && 'Intolerance'}
                    {allergy.category === 'adverse-reaction' && 'Adverse Reaction'}
                  </span>
                </div>
                <div className="cell reaction">
                  {allergy.reaction?.description || <span className="muted">Not specified</span>}
                </div>
                <div className="cell severity">
                  {allergy.reaction?.severity ? (
                    <span className={`severity-pill severity-${allergy.reaction.severity}`}>
                      {allergy.reaction.severity === 'mild' && 'Mild'}
                      {allergy.reaction.severity === 'moderate' && 'Moderate'}
                      {allergy.reaction.severity === 'severe' && 'Severe'}
                      {allergy.reaction.severity === 'life-threatening' && 'Life-threatening'}
                      {allergy.reaction.severity === 'unknown' && 'Unknown'}
                    </span>
                  ) : (
                    <span className="muted">Not recorded</span>
                  )}
                </div>
                <div className="cell recorded">
                  {allergy.recordedDate
                    ? formatDate(allergy.recordedDate)
                    : formatDate(allergy.createdAt)}
                </div>
                <div className="cell actions">
                  <button
                    className="btn-link btn-small"
                    onClick={() => onEdit(allergy)}
                    disabled={readOnly}
                    title={readOnly ? 'Secondary records are read-only' : undefined}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-link btn-small btn-danger"
                    onClick={() => onDeactivate(allergy)}
                    disabled={readOnly}
                    title={readOnly ? 'Secondary records are read-only' : undefined}
                  >
                    Deactivate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {inactiveAllergies.length > 0 && (
        <section className="allergy-section">
          <div className="section-header">
            <h3>Inactive / Historical Allergies</h3>
            <span className="badge muted">{inactiveAllergies.length}</span>
          </div>
          <div className="allergy-table inactive" role="table">
            <div className="allergy-table-header" role="row">
              <div className="cell trigger">Trigger</div>
              <div className="cell category">Category</div>
              <div className="cell reaction">Reaction</div>
              <div className="cell deactivated">Deactivated</div>
              <div className="cell reason">Reason</div>
              <div className="cell actions">Actions</div>
            </div>
            {inactiveAllergies.map((allergy) => (
              <div
                key={allergy.id}
                className="allergy-table-row inactive-row"
                role="row"
              >
                <div className="cell trigger">
                  <span className="trigger-name">{allergy.triggerDisplay}</span>
                </div>
                <div className="cell category">
                  <span className={`badge badge-${allergy.category}`}>
                    {allergy.category === 'true-allergy' && 'True Allergy'}
                    {allergy.category === 'intolerance' && 'Intolerance'}
                    {allergy.category === 'adverse-reaction' && 'Adverse Reaction'}
                  </span>
                </div>
                <div className="cell reaction">
                  {allergy.reaction?.description || <span className="muted">Not specified</span>}
                </div>
                <div className="cell deactivated">
                  {allergy.deactivatedAt ? formatDate(allergy.deactivatedAt) : '—'}
                </div>
                <div className="cell reason">
                  {allergy.deactivationReason || <span className="muted">Not recorded</span>}
                </div>
                <div className="cell actions" />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

